import React from 'react';
import { Bell, Shield, Zap, Timer, FileSearch, ArrowRightLeft, AlertCircle } from 'lucide-react';
import { useLanguage } from '../components/LanguageContext';

export function IncidentResponse() {
  const { language } = useLanguage();
  
  const content = {
    hero: {
      title: {
        fr: 'Réponse aux Incidents',
        en: 'Incident Response'
      },
      subtitle: {
        fr: 'Réponse rapide et efficace aux incidents de sécurité',
        en: 'Swift and effective security incident response'
      },
      description: {
        fr: `Notre service de réponse aux incidents combine expertise humaine et technologie avancée pour détecter, 
             analyser et neutraliser les menaces de sécurité en temps réel. Notre équipe d'experts est disponible 
             24/7 pour protéger votre infrastructure et vos données critiques.`,
        en: `Our incident response service combines human expertise and advanced technology to detect, analyze, 
             and neutralize security threats in real-time. Our team of experts is available 24/7 to protect 
             your infrastructure and critical data.`
      }
    },
    overview: {
      title: {
        fr: 'Comment Nous Protégeons Votre Entreprise',
        en: 'How We Protect Your Business'
      },
      content: {
        fr: `Notre approche de la réponse aux incidents repose sur trois piliers fondamentaux :
             la détection précoce, l'analyse approfondie et la réponse coordonnée. Nous utilisons
             des technologies de pointe comme l'intelligence artificielle et le machine learning pour
             identifier les menaces avant qu'elles ne causent des dommages significatifs.`,
        en: `Our incident response approach is built on three fundamental pillars: early detection,
             thorough analysis, and coordinated response. We use cutting-edge technologies like
             artificial intelligence and machine learning to identify threats before they cause
             significant damage.`
      }
    },
    features: [
      {
        icon: <Timer className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Réponse Rapide',
          en: 'Rapid Response'
        },
        description: {
          fr: 'Intervention immédiate 24/7 en cas d\'incident',
          en: '24/7 immediate incident intervention'
        },
        details: {
          fr: `Notre équipe de réponse est prête à intervenir à tout moment, avec un temps de réponse
               moyen de moins de 15 minutes. Nous utilisons des protocoles d'escalade automatisés et
               une coordination en temps réel pour assurer une réponse immédiate et efficace.`,
          en: `Our response team is ready to intervene at any time, with an average response time of
               less than 15 minutes. We use automated escalation protocols and real-time coordination
               to ensure immediate and effective response.`
        }
      },
      {
        icon: <FileSearch className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Investigation Approfondie',
          en: 'Thorough Investigation'
        },
        description: {
          fr: 'Analyse détaillée des causes et impacts',
          en: 'Detailed analysis of causes and impacts'
        },
        details: {
          fr: `Notre processus d'investigation combine l'analyse forensique avancée, la corrélation
               d'événements et l'intelligence des menaces pour comprendre la portée complète de
               l'incident et prévenir les récidives.`,
          en: `Our investigation process combines advanced forensic analysis, event correlation,
               and threat intelligence to understand the full scope of the incident and prevent
               recurrence.`
        }
      },
      {
        icon: <ArrowRightLeft className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Remédiation Complète',
          en: 'Complete Remediation'
        },
        description: {
          fr: 'Solutions efficaces et prévention future',
          en: 'Effective solutions and future prevention'
        },
        details: {
          fr: `Notre approche de remédiation va au-delà de la simple résolution de l'incident.
               Nous mettons en place des mesures préventives, améliorons les contrôles de sécurité
               et fournissons des recommandations détaillées pour renforcer votre posture de sécurité.`,
          en: `Our remediation approach goes beyond just resolving the incident. We implement
               preventive measures, improve security controls, and provide detailed recommendations
               to strengthen your security posture.`
        }
      }
    ],
    process: {
      title: {
        fr: 'Notre Processus',
        en: 'Our Process'
      },
      description: {
        fr: `Notre processus de réponse aux incidents est conçu pour minimiser les impacts et
             assurer une récupération rapide. Chaque étape est optimisée pour une efficacité maximale
             tout en maintenant la continuité des activités.`,
        en: `Our incident response process is designed to minimize impacts and ensure rapid recovery.
             Each step is optimized for maximum efficiency while maintaining business continuity.`
      },
      steps: [
        {
          icon: <AlertCircle className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Détection et Alerte',
            en: 'Detection and Alert'
          },
          description: {
            fr: 'Identification rapide des incidents de sécurité',
            en: 'Rapid identification of security incidents'
          },
          details: {
            fr: `Nos systèmes de détection avancés surveillent en permanence votre environnement,
                 utilisant l'IA pour identifier les anomalies et déclencher des alertes en temps réel.`,
            en: `Our advanced detection systems continuously monitor your environment, using AI to
                 identify anomalies and trigger real-time alerts.`
          }
        },
        {
          icon: <Shield className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Confinement',
            en: 'Containment'
          },
          description: {
            fr: 'Isolation immédiate des menaces',
            en: 'Immediate threat isolation'
          },
          details: {
            fr: `Nous isolons rapidement les systèmes affectés pour empêcher la propagation des menaces,
                 tout en maintenant les services critiques opérationnels.`,
            en: `We quickly isolate affected systems to prevent threat propagation while maintaining
                 critical services operational.`
          }
        },
        {
          icon: <Zap className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Éradication',
            en: 'Eradication'
          },
          description: {
            fr: 'Élimination complète des menaces',
            en: 'Complete threat elimination'
          },
          details: {
            fr: `Notre équipe utilise des outils spécialisés et des techniques avancées pour éliminer
                 complètement les menaces et leurs composants de votre environnement.`,
            en: `Our team uses specialized tools and advanced techniques to completely eliminate
                 threats and their components from your environment.`
          }
        },
        {
          icon: <Bell className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Récupération',
            en: 'Recovery'
          },
          description: {
            fr: 'Restauration des systèmes et données',
            en: 'System and data restoration'
          },
          details: {
            fr: `Nous restaurons vos systèmes à un état sécurisé, vérifions l'intégrité des données
                 et mettons en place des mesures pour prévenir les incidents similaires.`,
            en: `We restore your systems to a secure state, verify data integrity, and implement
                 measures to prevent similar incidents.`
          }
        }
      ]
    },
    capabilities: {
      title: {
        fr: 'Nos Capacités',
        en: 'Our Capabilities'
      },
      description: {
        fr: `Nos capacités de réponse aux incidents s'appuient sur des années d'expérience dans la
             gestion des menaces les plus sophistiquées. Nous combinons expertise technique,
             processus éprouvés et technologies de pointe.`,
        en: `Our incident response capabilities are built on years of experience handling the most
             sophisticated threats. We combine technical expertise, proven processes, and
             cutting-edge technologies.`
      },
      items: [
        {
          title: {
            fr: 'Analyse Forensique',
            en: 'Forensic Analysis'
          },
          description: {
            fr: 'Investigation approfondie des incidents de sécurité',
            en: 'In-depth investigation of security incidents'
          },
          details: {
            fr: `Notre équipe forensique utilise des outils spécialisés pour analyser les systèmes
                 compromis, reconstruire la chronologie des événements et identifier les indicateurs
                 de compromission.`,
            en: `Our forensic team uses specialized tools to analyze compromised systems, reconstruct
                 the timeline of events, and identify indicators of compromise.`
          }
        },
        {
          title: {
            fr: 'Réponse Automatisée',
            en: 'Automated Response'
          },
          description: {
            fr: 'Actions automatiques pour contenir les menaces',
            en: 'Automatic actions to contain threats'
          },
          details: {
            fr: `Nos systèmes automatisés peuvent réagir instantanément aux menaces détectées,
                 réduisant considérablement le temps de réponse et limitant l'impact des incidents.`,
            en: `Our automated systems can react instantly to detected threats, significantly
                 reducing response time and limiting incident impact.`
          }
        },
        {
          title: {
            fr: 'Documentation Détaillée',
            en: 'Detailed Documentation'
          },
          description: {
            fr: 'Rapports complets et recommandations',
            en: 'Comprehensive reports and recommendations'
          },
          details: {
            fr: `Nous fournissons une documentation complète de chaque incident, incluant l'analyse
                 des causes profondes, les actions prises et les recommandations pour renforcer
                 votre sécurité.`,
            en: `We provide complete documentation of each incident, including root cause analysis,
                 actions taken, and recommendations to strengthen your security.`
          }
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Hero Section with Extended Description */}
      <div className="hero-pattern py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Bell className="w-16 h-16 text-blue-500 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {content.hero.title[language]}
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              {content.hero.subtitle[language]}
            </p>
            <p className="text-lg text-gray-400 leading-relaxed">
              {content.hero.description[language]}
            </p>
          </div>
        </div>
      </div>

      {/* Overview Section */}
      <div className="container mx-auto px-6 py-20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            {content.overview.title[language]}
          </h2>
          <p className="text-lg text-gray-300 leading-relaxed">
            {content.overview.content[language]}
          </p>
        </div>
      </div>

      {/* Features Grid with Detailed Explanations */}
      <div className="container mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {content.features.map((feature, index) => (
            <div key={index} className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-lg border border-gray-700/50 hover:border-blue-500/50 transition-colors duration-300">
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-xl font-bold text-white mb-3">
                {feature.title[language]}
              </h3>
              <p className="text-gray-300 mb-4">
                {feature.description[language]}
              </p>
              <p className="text-gray-400 text-sm leading-relaxed">
                {feature.details[language]}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Process Section with Extended Information */}
      <div className="bg-gray-800/50 py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-6">
            {content.process.title[language]}
          </h2>
          <p className="text-lg text-gray-300 text-center max-w-4xl mx-auto mb-12 leading-relaxed">
            {content.process.description[language]}
          </p>
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <div className="absolute left-8 inset-y-0 w-0.5 bg-blue-500/30"></div>
              <div className="space-y-12">
                {content.process.steps.map((step, index) => (
                  <div key={index} className="relative flex items-start gap-8">
                    <div className="absolute left-8 w-3 h-3 bg-blue-500 rounded-full transform -translate-x-1.5 mt-2"></div>
                    <div className="pl-20">
                      <h3 className="text-xl font-bold text-white mb-2">
                        {step.title[language]}
                      </h3>
                      <p className="text-gray-300 mb-3">
                        {step.description[language]}
                      </p>
                      <p className="text-gray-400 text-sm leading-relaxed">
                        {step.details[language]}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Capabilities Section with In-depth Details */}
      <div className="container mx-auto px-6 py-20">
        <h2 className="text-3xl font-bold text-white text-center mb-6">
          {content.capabilities.title[language]}
        </h2>
        <p className="text-lg text-gray-300 text-center max-w-4xl mx-auto mb-12 leading-relaxed">
          {content.capabilities.description[language]}
        </p>
        <div className="max-w-4xl mx-auto space-y-8">
          {content.capabilities.items.map((item, index) => (
            <div key={index} className="flex items-start space-x-4 bg-gray-800/50 p-6 rounded-lg">
              <div className="flex-shrink-0 bg-blue-500/10 p-3 rounded-lg">
                <Shield className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  {item.title[language]}
                </h3>
                <p className="text-gray-300 mb-3">
                  {item.description[language]}
                </p>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {item.details[language]}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Visual Case Studies */}
      <div className="container mx-auto px-6 py-20">
        <h2 className="text-3xl font-bold text-white text-center mb-12">
          {language === 'fr' ? 'Études de Cas' : 'Case Studies'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&q=80" 
              alt="Ransomware Response"
              className="w-full h-48 object-cover"
            />
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'fr' ? 'Réponse Ransomware' : 'Ransomware Response'}
              </h3>
              <p className="text-gray-300">
                {language === 'fr' 
                  ? 'Intervention rapide et récupération complète des données'
                  : 'Rapid intervention and complete data recovery'}
              </p>
            </div>
          </div>
          <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/photo-1504639725590-34d0984388bd?auto=format&fit=crop&q=80" 
              alt="Network Breach"
              className="w-full h-48 object-cover"
            />
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'fr' ? 'Intrusion Réseau' : 'Network Breach'}
              </h3>
              <p className="text-gray-300">
                {language === 'fr' 
                  ? 'Détection et neutralisation d\'une menace APT'
                  : 'Detection and neutralization of an APT threat'}
              </p>
            </div>
          </div>
          <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/photo-1624705002806-5d72df19c3ad?auto=format&fit=crop&q=80" 
              alt="Data Protection"
              className="w-full h-48 object-cover"
            />
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'fr' ? 'Protection des Données' : 'Data Protection'}
              </h3>
              <p className="text-gray-300">
                {language === 'fr' 
                  ? 'Prévention d\'une fuite de données sensibles'
                  : 'Prevention of sensitive data breach'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="container mx-auto px-6 py-20">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1504639725590-34d0984388bd?auto=format&fit=crop&q=80')] opacity-10 bg-cover bg-center"></div>
          <div className="relative z-10">
            <h2 className="text-3xl font-bold text-white mb-4">
              {language === 'fr' ? 'Prêt à Sécuriser Votre Infrastructure ?' : 'Ready to Secure Your Infrastructure?'}
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              {language === 'fr' 
                ? 'Découvrez comment notre équipe peut vous protéger' 
                : 'Discover how our team can protect you'}
            </p>
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105">
              {language === 'fr' ? 'Contactez-nous' : 'Contact Us'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}